import { moveToFulfilled } from '@ms/yammer-data-support';
import { Reducer } from 'redux';

import { CampaignCreateFulfilledAction } from '../../domains/campaignCreation/actions.types';
import { CampaignEditFulfilledAction } from '../../domains/campaignEdit/actions.types';
import { CampaignLoadFulfilledAction } from '../../domains/campaigns/actions.types';
import {
  AddToDirectAudienceFulfilledAction,
  DesignateLeadersFromOfficeGroupAsyncStatusFulfilledAction,
  UserDirectAudienceLoadFulfilledAction,
  UserDirectAudienceLoadMoreFulfilledAction,
} from '../../domains/leadersManagement/actions.types';
import { TeamsMeetingLoadFulfilledAction } from '../../domains/teamsMeetingLoad/actions.types';
import { TeamsMeetingsByOrganizerFulfilledAction } from '../../domains/teamsMeetingsByOrganizer/actions.types';
import { TypeaheadFulfilledAction } from '../../domains/typeahead/actions.types';

import { OfficeGroup, OfficeGroups } from './types';

export type OfficeGroupAction =
  | AddToDirectAudienceFulfilledAction
  | DesignateLeadersFromOfficeGroupAsyncStatusFulfilledAction
  | TeamsMeetingLoadFulfilledAction
  | TeamsMeetingsByOrganizerFulfilledAction
  | TypeaheadFulfilledAction
  | UserDirectAudienceLoadFulfilledAction
  | UserDirectAudienceLoadMoreFulfilledAction
  | CampaignCreateFulfilledAction
  | CampaignEditFulfilledAction
  | CampaignLoadFulfilledAction;

const initialState: OfficeGroups = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

type StateWithFulfilledOfficeGroups = (
  state: OfficeGroups,
  officeGroupsResult: Record<string, OfficeGroup> | undefined
) => OfficeGroups;
const stateWithFulfilledOfficeGroups: StateWithFulfilledOfficeGroups = (state, officeGroupsResult) => {
  if (!officeGroupsResult) {
    return state;
  }

  const fulfilledOfficeGroupIds = new Set(Object.keys(state.fulfilled.byId));

  const newOfficeGroups = Object.keys(officeGroupsResult).reduce((entities, officeGroupIdResult) => {
    const officeGroupEntityResult: OfficeGroup = officeGroupsResult[officeGroupIdResult];

    if (fulfilledOfficeGroupIds.has(officeGroupIdResult)) {
      const fulfilledOfficeGroup = state.fulfilled.byId[officeGroupIdResult];

      return {
        ...entities,
        [officeGroupIdResult]: {
          ...officeGroupEntityResult,
          name: fulfilledOfficeGroup.name || officeGroupEntityResult.name,
          memberCount: fulfilledOfficeGroup.memberCount || officeGroupEntityResult.memberCount,
        },
      };
    }

    return {
      ...entities,
      [officeGroupIdResult]: officeGroupEntityResult,
    };
  }, {});

  return moveToFulfilled(state, newOfficeGroups);
};

const reducer: Reducer<OfficeGroups, OfficeGroupAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'USER_DIRECT_AUDIENCE.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.officeGroups);
    case 'TYPEAHEAD.LOAD_FULFILLED':
      return stateWithFulfilledOfficeGroups(state, action.payload.entities.officeGroups);
    case 'USER_DIRECT_AUDIENCE.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.officeGroups);
    case 'ADD_TO_DIRECT_AUDIENCE.FULFILLED':
      return moveToFulfilled(state, action.payload.entities.officeGroups);
    case 'DESIGNATE_LEADERS_FROM_OFFICE_GROUP_ASYNC_STATUS.FULFILLED':
      return stateWithFulfilledOfficeGroups(state, action.payload.entities.officeGroups);
    case 'TEAMS_MEETING.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.officeGroups);
    case 'TEAMS_MEETINGS_BY_ORGANIZER.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.officeGroups);
    case 'CAMPAIGN.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.officeGroups);
    case 'CAMPAIGN.CREATE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.officeGroups);
    case 'CAMPAIGN.EDIT_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.officeGroups);
    default:
      return state;
  }
};

export default reducer;
